import { type FC } from 'react';
import { useIntl } from 'dibs-react-intl';
import { createFragmentContainer, graphql } from 'react-relay/legacy.js';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext.tsx';
import { isDimensionFilter, getFilterValue } from '../sbSharedRefineMenuHelpers.ts';
import { useMeasurementUnit } from '../../useMeasurementUnit.ts';
import { useCurrency } from '../../../shared/hooks/useCurrency.ts';
import {
    getPreviouslyAppliedCategory,
    CATEGORY_LEVEL_1,
    CATEGORY_LEVEL_2,
} from '../../../utils/categoryHelpers.ts';
import { VERTICALS } from '../../../constants/uriConstants.ts';

import { SbSharedRefineMenuFacet } from '../SbSharedRefineMenuFacet/SbSharedRefineMenuFacet.tsx';
import { type RefineMenuFiltersMap } from '../sbSharedRefineMenuConstants.ts';

import { type SbSharedRefineMenuFilters_viewer$data } from './__generated__/SbSharedRefineMenuFilters_viewer.graphql.ts';
import { type SbSharedRefineMenuFilters_user$data } from './__generated__/SbSharedRefineMenuFilters_user.graphql.ts';
import { type SbSharedRefineMenuFilters_itemSearch$data } from './__generated__/SbSharedRefineMenuFilters_itemSearch.graphql.ts';
import { type SbSharedRefineMenuFilters_filters$data } from './__generated__/SbSharedRefineMenuFilters_filters.graphql.ts';

type Props = {
    viewer: SbSharedRefineMenuFilters_viewer$data;
    user: SbSharedRefineMenuFilters_user$data | null | undefined;
    itemSearch: SbSharedRefineMenuFilters_itemSearch$data;
    filters: SbSharedRefineMenuFilters_filters$data;
    filterMap: RefineMenuFiltersMap;
    initiallyExpanded: (filterName: string) => boolean;
    isClient?: boolean;
    uriRef?: string;
    isTrade?: boolean;
};

const SbSharedRefineMenuFiltersComponent: FC<Props> = ({
    user,
    viewer,
    filters,
    isClient,
    filterMap,
    itemSearch,
    initiallyExpanded,
}) => {
    let dimensionsDisplayed = false;
    const { isDesktop } = useServerVarsContext();
    const intl = useIntl();
    const [measurementUnit] = useMeasurementUnit({ user, itemSearch });
    const { currency } = useCurrency();
    const { topCategoryL1, appliedFilters } = itemSearch;
    const isArt = topCategoryL1 === VERTICALS.ART;
    const isTablesL2 =
        topCategoryL1 === VERTICALS.FURNITURE &&
        getFilterValue(appliedFilters, CATEGORY_LEVEL_2)?.urlLabel === 'tables';

    return (
        <>
            {filters?.map(filter => {
                const {
                    values,
                    totalCount,
                    name: filterName,
                    localizedFilterName: title,
                    originalFilterName,
                } = filter;

                if (!filterName) {
                    return null;
                }

                const { component: FilterComponent, title: FilterTitle } =
                    filterMap[filterName] || {};
                const titleOverride = FilterTitle && intl.formatMessage(FilterTitle);
                const trackingTrigger = FilterTitle
                    ? FilterTitle.defaultMessage
                    : originalFilterName;
                /**
                 * height, width, length and depth are returned as separate filters but need to be
                 * displayed in one "Dimensions" facet.  When the first valid dimension type filter is hit
                 * we will setup the dimensions facet.  Once the dimensions facet is setup we will
                 * ignore the rest of the dimension filters.
                 *
                 * The dimensions filter component will account and setup all available dimension filters in one facet
                 *
                 * Art filter uses a custom component, in art category dimensions will be grouped with artSize
                 */

                if (isDimensionFilter(filterName)) {
                    if (isArt || dimensionsDisplayed) {
                        return null;
                    }
                    dimensionsDisplayed = true;
                }

                const appliedFilter = isDesktop
                    ? null
                    : appliedFilters?.find(f => f?.name === filterName);
                let selectedValues = appliedFilter?.values || [];

                if (filterName === CATEGORY_LEVEL_1 && selectedValues?.length) {
                    const lowestCategory = getPreviouslyAppliedCategory(appliedFilters || []);
                    if (lowestCategory) {
                        selectedValues = lowestCategory.values || [];
                    }
                }
                const maxStatsRange = isDimensionFilter(filterName) ? values?.[0]?.displayName : '';

                return (
                    FilterComponent && (
                        <SbSharedRefineMenuFacet
                            key={filterName}
                            storageFacetKey={filterName}
                            title={titleOverride || title || ''}
                            trackingTrigger={trackingTrigger}
                            titleMessage={title || ''}
                            initiallyExpanded={initiallyExpanded(filterName)}
                            selectedValues={selectedValues}
                            measurementUnit={measurementUnit}
                            maxStatsRange={maxStatsRange}
                            currency={currency}
                            appliedFiltersComponent={null}
                            isTablesL2={isTablesL2}
                        >
                            <FilterComponent
                                user={user}
                                viewer={viewer}
                                values={values}
                                filter={filter}
                                filters={filters}
                                currency={currency}
                                isClient={isClient}
                                totalCount={totalCount}
                                filterName={filterName}
                                itemSearch={itemSearch}
                                shouldUseExpandableList={false}
                            />
                        </SbSharedRefineMenuFacet>
                    )
                );
            })}
        </>
    );
};

export const SbSharedRefineMenuFilters = createFragmentContainer(
    SbSharedRefineMenuFiltersComponent,
    {
        viewer: graphql`
            fragment SbSharedRefineMenuFilters_viewer on Viewer {
                ...SbSharedRefineMenuFilterMetal_viewer
                ...SbSharedRefineMenuFilterStone_viewer
                ...SbSharedRefineMenuFilterOrigin_viewer
                ...SbSharedRefineMenuFilterCreator_viewer
                ...SbSharedRefineMenuFilterMaterial_viewer
                ...SbSharedRefineMenuFilterArtSubject_viewer
                ...SbSharedRefineMenuFilterItemLocation_viewer
                ...SbRespRefineMenuFilterStoneCut_viewer
                ...SbMobileRefineMenuFilterStoneCut_viewer
            }
        `,
        itemSearch: graphql`
            fragment SbSharedRefineMenuFilters_itemSearch on ItemSearchQueryConnection {
                ...SbRespRefineMenuFilterPrice_itemSearch
                ...SbSharedRefineMenuFilterStone_itemSearch
                ...SbSharedRefineMenuFilterMetal_itemSearch
                ...SbSharedRefineMenuFilterColor_itemSearch
                ...SbMobileRefineMenuFilterPrice_itemSearch
                ...SbSharedRefineMenuFilterStyle_itemSearch
                ...SbSharedRefineMenuFilterArtSubject_itemSearch
                ...SbSharedRefineMenuFilterOrigin_itemSearch
                ...SbSharedRefineMenuFilterPeriod_itemSearch
                ...SbRespRefineMenuFilterCategory_itemSearch
                ...SbRespRefineMenuFilterStoneCut_itemSearch
                ...SbSharedRefineMenuFilterCreator_itemSearch
                ...SbMobileRefineMenuFilterStoneCut_itemSearch
                ...SbMobileRefineMenuFilterCategory_itemSearch
                ...SbSharedRefineMenuFilterMaterial_itemSearch
                ...SbSharedRefineMenuFilterItemLocation_itemSearch
                ...SbSharedRefineMenuFilterDeliveryOptions_itemSearch
                ...SbSharedRefineMenuFilterItemType_itemSearch
                ...SbSharedRefineMenuFilterJewelryType_itemSearch
                ...SbRespRefineMenuFilterDimensions_itemSearch
                ...SbRespRefineMenuFilterCaratWeight_itemSearch
                ...SbMobileRefineMenuFilterDimensions_itemSearch
                ...SbMobileRefineMenuFilterCaratWeight_itemSearch
                ...SbSharedRefineMenuFilterAvailability_itemSearch
                ...SbSharedRefineMenuFilterMatchingSets_itemSearch
                ...SbSharedRefineMenuFilterGender_itemSearch
                ...SbSharedRefineMenuFilterFraming_itemSearch
                ...SbSharedRefineMenuFilterFramingOld_itemSearch
                ...SbSharedRefineMenuFilterRingSize_itemSearch
                ...SbMobileRefineMenuFilterArtSize_itemSearch
                ...SbRespRefineMenuFilterArtSize_itemSearch
                ...SbSharedRefineMenuFilterArtOrientation_itemSearch
                ...SbSharedRefineMenuFilterMovement_itemSearch
                ...useMeasurementUnit_itemSearch
                topCategoryL1
                appliedFilters {
                    name
                    values {
                        displayName
                        urlLabel
                    }
                }
            }
        `,
        filters: graphql`
            fragment SbSharedRefineMenuFilters_filters on SearchBrowseFilter @relay(plural: true) {
                name
                totalCount
                localizedFilterName
                originalFilterName
                ...SbRespRefineMenuFilterCategory_filters
                ...SbMobileRefineMenuFilterCategory_filters
                ...SbRespRefineMenuFilterDimensions_filters
                ...SbMobileRefineMenuFilterDimensions_filters
                ...SbRespRefineMenuFilterArtSize_filters
                ...SbMobileRefineMenuFilterArtSize_filters
                ...SbSharedRefineMenuFilterRingSize_filters
                ...SbMobileRefineMenuFilterPrice_filters
                ...SbRespRefineMenuFilterPrice_filters
                values {
                    displayName
                    urlLabel
                    count
                    code
                    linkReference
                    linkable
                    hexCode
                    properties {
                        min
                        max
                    }
                }
            }
        `,
        user: graphql`
            fragment SbSharedRefineMenuFilters_user on User {
                ...SbRespRefineMenuFilterPrice_user
                ...SbRespRefineMenuFilterDimensions_user
                ...SbMobileRefineMenuFilterPrice_user
                ...SbMobileRefineMenuFilterDimensions_user
                ...SbMobileRefineMenuFilterArtSize_user
                ...SbRespRefineMenuFilterArtSize_user
                ...useMeasurementUnit_user
                ...SbSharedRefineMenuFilterDeliveryOptions_user
            }
        `,
    }
);
